import { colorPrimary, colorSecondary } from "@/constants/colors";
import { Hint } from "@/HOC";
import { useTranslate } from "@/hooks";
import { selectHintsEnabled } from "@/store/hintsSlice";
import { useAppSelector } from "@/store/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuItem, Select } from "@mui/material";
import styles from "./IconSelect.module.scss";
import { IIconSelect } from "./IconSelect.props";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"; // Добавьте этот импорт

const IconSelect = <T extends string>({
	value,
	disabled,
	onChange,
	iconList,
	itemsList,
	haveHint = false,
	mode = "fullIcon",
	initialText = "",
	hintPrefix = "",
	mainIconColor = colorSecondary,
	withBorder = true,
	widthHeightSize = 31,
	menuFontmultiplierFromWidth = 2.6,
}: IIconSelect<T>) => {
	const hintsEnabled = useAppSelector(selectHintsEnabled);
	const translate = useTranslate();
	const menuFontSize = widthHeightSize / menuFontmultiplierFromWidth;
	const iconSize = widthHeightSize / 2;

	// Функция для проверки, является ли значение IconDefinition
	const isIconDefinition = (value: any): value is IconDefinition => {
		return value && typeof value === "object" && "icon" in value && "prefix" in value;
	};

	return (
		<Select
			// label={translate("select_action")}
			disabled={disabled}
			value={value}
			onChange={onChange}
			IconComponent={() => null}
			renderValue={(selected) => {
				const value = selected as string;
				const title = translate(itemsList.find((el) => el.value === value)?.label ?? "");
				const icon = iconList[value as keyof typeof iconList];

				return (
					<Hint hint={haveHint ? translate(`${hintPrefix}${value.charAt(0).toUpperCase() + value.slice(1)}`) : ""} position="top" width="100%" textOverflow={true}>
						<div
							style={
								mode === "fullIcon"
									? {
											width: `${widthHeightSize}px`,
											height: `${widthHeightSize}px`,
											minWidth: `${widthHeightSize}px`,
											minHeight: `${widthHeightSize}px`,
											maxWidth: `${widthHeightSize}px`,
											maxHeight: `${widthHeightSize}px`,
											justifyContent: "center",
											display: "flex",
											alignItems: "center",
											gap: 4,
											color: mainIconColor,
									  }
									: {
											display: "inline-block",
											color: mainIconColor,
											alignItems: "center",
											justifyContent: "center",
									  }
							}
							title={hintsEnabled ? "" : title}
						>
							{mode === "fullIcon" ? (
								typeof icon === "string" ? (
									<span style={{ whiteSpace: "pre" }}>{icon}</span>
								) : isIconDefinition(icon) ? (
									<FontAwesomeIcon icon={icon} size="lg" style={{ fontSize: `${iconSize}px` }} />
								) : null
							) : (
								initialText
							)}
						</div>
					</Hint>
				);
			}}
			MenuProps={{
				anchorOrigin: {
					vertical: "bottom",
					horizontal: "left", // левый край меню совпадает с левым краем селекта
				},
				transformOrigin: {
					vertical: "top",
					horizontal: "left", // раскрытие от верхнего левого угла
				},
				TransitionProps: {
					timeout: 0, // мгновенное открытие
				},
			}}
			sx={{
				fontSize: 13,
				height: mode === "fullIcon" ? widthHeightSize : 13,
				color: colorPrimary,
				padding: 0, // убирает padding у корневого элемента Select
				"& .MuiOutlinedInput-input": { padding: "0 !important", paddingRight: 0 },

				border: `none`,
				"& .MuiOutlinedInput-notchedOutline": {
					border: `none`, // убирает рамку у Outlined
				},
				"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
					border: `none`, // убирает рамку при фокусе
				},
				boxShadow: "none", // если есть тень
				"& .MuiSelect-select":
					mode === "fullIcon"
						? {
								padding: "0 !important",
								paddingRight: 0,
								color: colorSecondary,
								width: `${widthHeightSize}px`,
								height: `${widthHeightSize}px`,
								minWidth: `${widthHeightSize}px`,
								minHeight: `${widthHeightSize}px`,
								maxWidth: `${widthHeightSize}px`,
								maxHeight: `${widthHeightSize}px`,
								borderRadius: "8px",
								border: withBorder ? `1px solid ${mainIconColor}` : "none",
								outline: "none",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								backgroundColor: "rgba(0, 0, 0, 0)",
								transition: "0.3s",
								cursor: disabled ? "default" : "pointer",
								willChange: "transform",
								"&:focus": {
									border: withBorder ? `1px solid ${colorSecondary}` : "none",
									outline: `1px solid ${colorSecondary}`,
								},
						  }
						: { padding: "0 !important" },
			}}
		>
			{itemsList.map(({ value, label }) => {
				const icon = iconList[value];
				const iconElement =
					typeof icon === "string" ? (
						<span style={{ whiteSpace: "pre" }}>{icon}</span>
					) : isIconDefinition(icon) ? (
						<FontAwesomeIcon
							icon={icon}
							width={iconSize}
							height={iconSize}
							size="lg"
							color={colorPrimary}
							style={{ width: `${iconSize}px`, fontSize: `${iconSize}px !important` }}
							title={translate(label)}
						/>
					) : null;

				return (
					<MenuItem
						key={value}
						value={value}
						sx={{
							fontSize: menuFontSize,
							color: colorPrimary,
							textAlign: "left",
							padding: "4px 9px", // убираем padding полностью
							margin: "auto",
							"&.MuiMenuItem-gutters": {
								padding: "4px 9px", // на всякий случай
								margin: "auto",
							},
							display: "flex", // чтобы иконка + текст корректно выстраивались
							alignItems: "center",
						}}
					>
						{haveHint ? (
							<Hint hint={translate(`${hintPrefix}${value.charAt(0).toUpperCase() + value.slice(1)}`)} position="left" width="100%" textOverflow={true}>
								<div className={styles.actionSelectIconItem}>
									{iconElement}
									<span>{translate(label)}</span>
								</div>
							</Hint>
						) : (
							<div className={styles.actionSelectIconItem}>
								{iconElement}
								<span>{translate(label)}</span>
							</div>
						)}
					</MenuItem>
				);
			})}
		</Select>
	);
};

export default IconSelect;
